import { rem } from 'polished'
import { useCallback, useContext } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import ClickableLink from 'components/Common/Clickable/ClickableLink'
import LayoutContainer from 'components/Common/LayoutContainer'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import ModalContext from 'contexts/ModalContext'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import LoyaltyPointsBalanceCard from 'loyalty/components/LoyaltyPointsBalanceCard'
import { LOYALTY_LANDING_PAGE } from 'loyalty/constants/loyaltyRoutes'
import { tierGradientBackgroundStyle, tierNormalContrastColorStyle } from 'styles/utils/tierStyles'

interface Props {
  isLoggedIn: boolean
  isAURegion: boolean
  points: number
  programName: string
  userTier: App.LoyaltyTier
}

const Hero = styled.section`
  ${tierGradientBackgroundStyle}
  ${tierNormalContrastColorStyle}
`

const HeroInner = styled(LayoutContainer)`
  padding-block: ${rem(40)};
  text-align: center;

  ${mediaQueryUp.tablet} {
    padding-block: ${rem(36)} ${rem(40)};
  }
`

const Breadcrumbs = styled(Group)`
  display: none;
  margin-bottom: ${rem(24)};
  text-align: start;

  ${mediaQueryUp.tablet} {
    display: flex;
  }
`

const CrumbLink = styled(ClickableLink)`
  color: inherit;
  text-decoration: underline;
`

const HeroCopy = styled(Group)`
  max-width: ${rem(335)};
  margin: 0 auto;

  ${mediaQueryUp.tablet} {
    max-width: ${rem(720)};
  }
`

const HeroHeadingWrapper = styled.div`
  max-width: ${rem(300)};

  ${mediaQueryUp.tablet} {
    max-width: none;
  }
`

const LoggedInHeroContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rem(24)};
  max-width: ${rem(900)};
  margin: 0 auto;

  ${mediaQueryUp.desktop} {
    display: grid;
    grid-template-columns: minmax(0, ${rem(440)}) ${rem(383)};
    justify-content: space-between;
    align-items: center;
    gap: ${rem(40)};
    text-align: start;
  }
`

const LoggedInHeroCopy = styled(Group)`
  max-width: ${rem(440)};
  text-align: start;
  align-items: flex-start;

  ${mediaQueryUp.desktop} {
    align-items: flex-start;
  }
`

const LoginButton = styled(TextButton)`
  && {
    min-width: ${rem(236)};
  }

  ${mediaQueryUp.tablet} {
    && {
      margin-top: ${rem(8)};
      min-width: ${rem(210)};
    }
  }
`

function LoyaltySpendPointsHero({ isLoggedIn, isAURegion, points, programName, userTier }: Props) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const onLoginClick = useCallback(() => {
    showModal(<AccountAccessModal />)
  }, [showModal])
  const membershipLabel = intl.formatMessage({
    id: 'uRWoWo5Wm-BrKsEhVikJ',
    defaultMessage: 'Your Membership',
    description: 'Breadcrumb link label for the Societe membership page',
  })
  const pageTitle = intl.formatMessage({
    id: '2bN6atJnR1_yrKjTCLUE',
    defaultMessage: 'Using my {programName} Points',
    description: 'Main heading for the Societe spend points page hero section',
  }, { programName })
  const heroSubheading = isAURegion
    ? intl.formatMessage({
        id: 'Q6Px1rVjSnagtWRQqDuI',
        defaultMessage: 'Your points, your way — spend them on any booking or redeem them for exclusive Reward offers.',
        description: 'Hero subheading for the Societe spend points page explaining redemption options in Australia',
      })
    : intl.formatMessage({
        id: 'xtXA1vdWic2ljW-1ga82',
        defaultMessage: 'Use your points to pay for all or part of your next escape — simply choose how many to apply at checkout and save instantly.',
        description: 'Hero subheading for the Societe spend points page in regions where Societe Rewards offers are unavailable',
      })

  return <ProductPaletteProvider palette={`tier-${userTier}`}>
    <Hero className={`tier-${userTier}`}>
      <HeroInner variant="large">
        <Breadcrumbs direction="horizontal" gap={8} verticalAlign="center">
          <CrumbLink to={`/${LOYALTY_LANDING_PAGE}`}>
            <BodyText variant="small">{membershipLabel}</BodyText>
          </CrumbLink>
          <LineAngleRightIcon size={12} />
          <BodyText variant="small">
            {intl.formatMessage({
              id: 'aZE7f6fq1EIiKpuLLcyq',
              defaultMessage: 'Using my points',
              description: 'Current breadcrumb label for the Societe spend points page',
            })}
          </BodyText>
        </Breadcrumbs>

        {isLoggedIn ? <LoggedInHeroContent>
          <LoggedInHeroCopy direction="vertical" gap={12} horizontalAlign="start">
            <Heading variant="heading1" as="h1">
              <FormattedMessage
                id="MYGf9w-PE3CF4C31WSza"
                defaultMessage="Using my<br></br>{programName} Points"
                description="Main heading for the Societe spend points page hero section"
                values={{
                  programName,
                  br: () => <br />,
                }}
              />
            </Heading>
            <BodyText variant="large">{heroSubheading}</BodyText>
          </LoggedInHeroCopy>
          <LoyaltyPointsBalanceCard points={points} programName={programName} userTier={userTier} />
        </LoggedInHeroContent> : <HeroCopy direction="vertical" gap={12} horizontalAlign="center">
          <HeroHeadingWrapper>
            <Heading variant="heading1" as="h1">{pageTitle}</Heading>
          </HeroHeadingWrapper>
          <BodyText variant="large">{heroSubheading}</BodyText>
          <LoginButton kind="secondary" variant="default" size="medium" onClick={onLoginClick}>
            {intl.formatMessage({
              id: 'gwIKf8h2GwTVLD4Lres2',
              defaultMessage: 'Log in to view points balance',
              description: 'CTA button on the Societe spend points page hero prompting login',
            })}
          </LoginButton>
        </HeroCopy>}
      </HeroInner>
    </Hero>
  </ProductPaletteProvider>
}

export default LoyaltySpendPointsHero
